import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

const GLOBAL_VIDEO_MUTED_KEY = 'GLOBAL_VIDEO_MUTED_KEY';

// Context type
type GlobalVideoContextType = {
  isMuted: boolean;
  setIsMuted: (muted: boolean) => void;
};

const GlobalVideoContext = createContext<GlobalVideoContextType | undefined>(
  undefined,
);

export const GlobalVideoContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isMuted, setIsMutedState] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(GLOBAL_VIDEO_MUTED_KEY);
      // by default, we want to mute the video
      return stored === null ? true : stored === 'true';
    }
    return true;
  });

  // Sync to localStorage
  useEffect(() => {
    localStorage.setItem(GLOBAL_VIDEO_MUTED_KEY, String(isMuted));
  }, [isMuted]);

  // Listen for changes in other tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === GLOBAL_VIDEO_MUTED_KEY && e.newValue !== null) {
        setIsMutedState(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setIsMuted = (muted: boolean) => {
    setIsMutedState(muted);
  };

  return (
    <GlobalVideoContext.Provider value={{ isMuted, setIsMuted }}>
      {children}
    </GlobalVideoContext.Provider>
  );
};

export const useGlobalVideoContext = () => {
  const ctx = useContext(GlobalVideoContext);
  if (!ctx) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        'useGlobalVideoContext must be used within a GlobalVideoContextProvider',
      );
    }
    // Fallback: default to muted
    return { isMuted: true, setIsMuted: () => {} };
  }
  return ctx;
};
